---
meta:
  - title: Como usar clientes - IUSPRO Docs
  - description: Aprenda a crear y trabajar con clientes en IUSPRO.
---

# Como usar clientes

Los clientes están pensados para que pueda trabajar un mismo cliente en varios chats sin perder el contexto.

## Paso 1: Cree el cliente

Puede crear un cliente desde la sección de clientes.

Lo más importante es definir:

- **nombre**
- descripción, si quiere
- color, si le ayuda a identificarlo
- instrucciones del cliente

Las instrucciones son útiles para decirle a la IA cómo quiere trabajar con ese cliente. Por ejemplo:

- que responda con tono técnico
- que priorice estrategia procesal
- que sea breve
- que compare varias opciones antes de concluir

## Paso 2: Adjunte contexto

Dentro del cliente puede agregar contexto permanente, como:

- archivos
- expedientes

Ese material queda asociado al cliente y puede usarse en chats nuevos dentro de ese mismo espacio.

## Paso 3: Cree chats dentro del cliente

Cuando crea un chat desde el cliente:

- el chat queda dentro de ese cliente
- hereda el contexto del cliente
- mantiene ese contexto sin que usted tenga que repetirlo

Esto le permite separar el trabajo por conversación.

Por ejemplo:

- un chat para resumen del caso
- un chat para redactar un escrito
- un chat para estrategia
- un chat para revisar un documento del cliente

## Paso 4: Mueva chats existentes

Si ya tiene un chat creado y luego decide organizarlo mejor, puede moverlo a un cliente.

Eso sirve mucho cuando:

- empezó una consulta rápida
- luego se convirtió en un trabajo más continuo con ese cliente
- y ahora quiere agruparlo con más chats y material

## Paso 5: Comparta el cliente si trabaja en equipo

Esta función está disponible solo en **planes empresariales**.

Si varias personas trabajan para el mismo cliente, puede compartir el cliente.

Eso permite que otras personas usen:

- las instrucciones del cliente
- los archivos del cliente
- los expedientes vinculados

Pero hay una regla importante:

- cada persona ve solo sus propios chats dentro del cliente

Además, puede decidir si otra persona tendrá permiso de:

- **ver**: puede usar el cliente y crear sus propios chats
- **editar**: además puede cambiar instrucciones, archivos y expedientes del cliente

## Paso 6: Use el cliente también en el Redactor

Si necesita redactar para ese cliente, puede crear el documento dentro del cliente.

Eso ayuda porque el Redactor puede aprovechar:

- las instrucciones del cliente
- los expedientes vinculados
- los archivos ya adjuntos

Así no tiene que repetir el contexto cada vez que redacta un nuevo documento del mismo cliente.

## Buenas prácticas

Para que los clientes le sean realmente útiles:

- use un nombre claro
- cree un cliente por cliente real, no por pregunta aislada
- mantenga instrucciones simples y estables
- suba solo los archivos que de verdad aportan contexto
- use varios chats dentro del mismo cliente en vez de mezclar todo en uno solo

## Siguiente lectura

- Lea [qué es un cliente](/guias/proyectos-que-es)
- Lea [cómo compartir clientes](/guias/proyectos-compartir-proyectos)
- Lea [clientes y expedientes](/guias/proyectos-y-expedientes)
- Vea [usar clientes en el redactor](/guias/redactor-usar-proyectos)
